import { useCallback, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  styled,
  Hidden,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { motion } from "framer-motion";
import base from "../../api/apis";
import { useReactQuery } from "../../hooks/useReactQuery";
import { fullName } from "../../utils/functions";

import LoadingAgentCard from "../LoadingAgentCard";
import AgentProfileCard from "./AgentProfileCard";
import EmptyAgentListView from "../EmptyListView";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const SyletedNavBtn = styled(Button)(({ theme }) => ({
  width: "2.5rem",
  minWidth: "2.5rem",
  height: "2.5rem",
  padding: 0,
  margin: 0,
  borderRadius: "2.5rem",
  backgroundColor: "white",
  border: "1px solid",
  borderColor: theme.palette.alternate.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
}));

const AgentsList = ({
  showHeader = true,
  title = "Need An Agent?",
  subTitle = "BPO Homes has Top Agents in your Area",
  agentList = null,
  isLoading = false,
  featured = false,
  browseMoreText = "Browse more agents",
  browseMoreAction,
  autoSlides = false,
  emptyMessage = "No agents found",
  styles = {},
  contentStyles = {
    px: 2.5,
  },
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const swiperRef = useRef(null);

  const fetchAgents = useCallback(async () => {
    try {
      // const { data } = await base.get("/register/agents/");
      // return data.results;
      const { data } = await base.get(
        "/register/random_paid_agent?page_size=12"
      );
      return data;
    } catch (error) {
      return error;
    }
  }, []);

  const { isFetching: load, data } = useReactQuery(["agents"], fetchAgents, {
    enabled: agentList === null && isLoading === false,
  });

  const agents = agentList ?? data;

  const hasMoreSlides = useMemo(() => {
    return agents?.length > 0 && (sm ? agents?.length > 4 : agents?.length > 1);
  }, [sm, agents]);

  const handleNavigation = (type) => {
    if (swiperRef.current) {
      switch (type) {
        case "prev":
          swiperRef.current.slidePrev();
          break;
        case "next":
          swiperRef.current.slideNext();
          break;
        default:
          break;
      }
    }
  };

  const BrowseMore = () => (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      disableElevation
      sx={{
        "&": { borderColor: "primary.main" },
        borderRadius: "50px",
        textTransform: "capitalize",
        padding: "12px 20px",
        height: "3rem",
        color: "text.orange.1",
        width: { xs: "100%", md: "auto" },
      }}
      onClick={() => browseMoreAction ?? navigate("/agents")}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <Typography fontSize={"1rem"}>{browseMoreText}</Typography>
        <ArrowForwardRoundedIcon sx={{ color: "text.orange.1" }} />
      </Stack>
    </Button>
  );

  return (
    <Box
      sx={{
        py: 5,
        backgroundColor: "paper.main",
        ...styles,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {showHeader && (
          <Container
            className="head-wrapper"
            maxWidth="xl"
            sx={{
              position: "relative",
              ...contentStyles,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: { xs: 2, md: 3 },
              }}
            >
              <Stack gap={1}>
                <Typography
                  variant="h4"
                  component="h2"
                  fontWeight="600"
                  fontSize={{ xs: "1.75rem", md: "2rem" }}
                >
                  {title}
                </Typography>
                {!featured && subTitle && (
                  <Typography
                    variant="body1"
                    fontSize={{ xs: "1rem:", sm: "18px" }}
                    color={"neutral.dark"}
                  >
                    {subTitle}
                  </Typography>
                )}
              </Stack>

              {!featured && (
                <Hidden mdDown>
                  {browseMoreText && data?.length > 0 && <BrowseMore />}
                </Hidden>
              )}
            </Box>
          </Container>
        )}

        <Box className="sw-wrapper" sx={{ position: "relative" }}>
          <Container
            maxWidth="xl"
            sx={{
              ...contentStyles,
            }}
          >
            <Swiper
              modules={[Navigation]}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              loop={hasMoreSlides}
              slidesPerView={autoSlides ? "auto" : undefined}
              spaceBetween={autoSlides ? 16 : undefined}
              breakpoints={
                autoSlides
                  ? undefined
                  : {
                      0: {
                        slidesPerView: 1.1,
                        spaceBetween: 10,
                        centeredSlides: true,
                        centeredSlidesBounds: true,
                      },
                      600: {
                        slidesPerView: 2,
                        spaceBetween: 10,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                      900: {
                        slidesPerView: 3,
                        spaceBetween: 16,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                      1050: {
                        slidesPerView: 3.5,
                        spaceBetween: 16,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                      1200: {
                        slidesPerView: 4,
                        spaceBetween: 16,
                        centeredSlides: false,
                        centeredSlidesBounds: false,
                      },
                    }
              }
              style={{
                padding: `8px 0 ${hasMoreSlides ? "1.5rem" : "0"} 0`,
              }}
            >
              {load || isLoading ? (
                Array(4)
                  .fill()
                  .map((_, ld) => {
                    return (
                      <SwiperSlide
                        key={ld}
                        style={{
                          width: sm ? "400px" : "315px",
                        }}
                      >
                        <LoadingAgentCard />
                      </SwiperSlide>
                    );
                  })
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  {agents?.length > 0 ? (
                    agents?.map((agent) => {
                      const randRatings = [4.95, 4.94, 4.93];
                      const randomIndex = Math.floor(
                        Math.random() * randRatings.length
                      );
                      return (
                        <SwiperSlide
                          key={agent?.id}
                          style={{
                            width: sm ? "360px" : "315px",
                          }}
                        >
                          <motion.div
                            key={agent?.id}
                            whileHover={{ y: -5 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <AgentProfileCard
                              id={agent?.id}
                              avatarSrc={agent?.profile_image}
                              name={fullName(agent)}
                              status={`BPO ${
                                agent.subscription ? "Premier" : "Partner"
                              } Agent`}
                              rating={randRatings[randomIndex]}
                              reviewCount={
                                agent?.review_count
                                  ? agent?.review_count
                                  : `99+`
                              }
                              specialties={
                                agent?.specialties?.length > 0
                                  ? agent?.specialties.map(
                                      (specialty) => specialty.title
                                    )
                                  : []
                              }
                              location={
                                agent?.brokerage_address ?? agent?.address
                              }
                              phone={agent?.phone}
                              email={agent?.user?.email}
                              featured={featured}
                            />
                          </motion.div>
                        </SwiperSlide>
                      );
                    })
                  ) : (
                    <Box pr={2.5}>
                      <EmptyAgentListView
                        message={emptyMessage}
                        suggestion=""
                      />
                    </Box>
                  )}
                </motion.div>
              )}
            </Swiper>
          </Container>

          <Container
            maxWidth="xl"
            sx={{
              pb: hasMoreSlides ? 0 : 3,
              ...contentStyles,
            }}
          >
            <Box sx={{ display: hasMoreSlides ? "block" : "none" }}>
              {!(load || isLoading) && (
                <Hidden mdDown>
                  <Stack
                    width="100%"
                    direction={"row"}
                    justifyContent={"flex-end"}
                    alignItems={"center"}
                    gap={2.5}
                  >
                    <SyletedNavBtn
                      color="alternate"
                      onClick={() => handleNavigation("prev")}
                    >
                      <ArrowBackOutlinedIcon color="alternate" />
                    </SyletedNavBtn>
                    <SyletedNavBtn
                      color="alternate"
                      onClick={() => handleNavigation("next")}
                    >
                      <ArrowForwardRoundedIcon color="alternate" />
                    </SyletedNavBtn>
                  </Stack>
                </Hidden>
              )}
              {!featured && (
                <Hidden mdUp>
                  {browseMoreText && data?.length > 0 && <BrowseMore />}
                </Hidden>
              )}
            </Box>
          </Container>
        </Box>
      </motion.div>
    </Box>
  );
};

export default AgentsList;
